import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { HelpCircle, TrendingUp } from 'lucide-react';

import { formatCompactCurrency } from '@client/util/formatCompactCurrency';

interface HeaderPreviewProps {
  isIncomeStream: boolean;
  existingAssetsValue: number | null | undefined;
  projectedValue: number | null;
  ageAtLtc: number | null | undefined;
  ltcAtYear: number | null | undefined;
  onShowFormula: () => void;
}

/**
 * Compact projected-value badge rendered next to the modal title, plus a
 * help-icon button that opens the calculation-formula sheet. Mirrors the
 * `previewBadgeContent` rendered by the guided-planning AccountCard so both
 * surfaces feel the same.
 */
export function HeaderPreview({
  isIncomeStream,
  existingAssetsValue,
  projectedValue,
  ageAtLtc,
  ltcAtYear,
  onShowFormula,
}: HeaderPreviewProps) {
  const tooltipCopy = isIncomeStream
    ? 'Total after-tax retirement income expected over the entire care period, starting at predicted long-term care age. The annual growth % is a cost-of-living adjustment applied to your income, not an investment return.'
    : 'Current account value and projected value at predicted long-term care age, based on your growth and tax assumptions.';

  const ageLabel = ageAtLtc ?? ltcAtYear;

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex min-w-0 max-w-full shrink cursor-default">
            <Badge
              variant="secondary"
              className="font-normal"
            >
              {isIncomeStream ? (
                <>
                  <span className="inline-flex items-center font-semibold">
                    <TrendingUp className="mr-1 size-3.5 shrink-0" />
                    {formatCompactCurrency(projectedValue)}
                  </span>
                  <span className="text-foreground ml-1">total (age {ageLabel})</span>
                </>
              ) : (
                <>
                  <span className="text-foreground">
                    {existingAssetsValue != null ? formatCompactCurrency(existingAssetsValue) : '—'}
                  </span>
                  <span className="mx-1"></span>
                  <span className="inline-flex items-center font-semibold">
                    <TrendingUp className="mr-1 size-3.5 shrink-0" />
                    {formatCompactCurrency(projectedValue)}
                  </span>
                  <span className="text-foreground ml-1">(age {ageLabel})</span>
                </>
              )}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs"
        >
          <p>{tooltipCopy}</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            onClick={onShowFormula}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onShowFormula();
            }}
            className="text-muted-foreground hover:text-primary shrink-0 cursor-pointer"
          >
            <HelpCircle className="size-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>How is this calculated?</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
